/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled, { css } from 'styled-components';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useState,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { createSolid, hasGradient } from '@web-stories-wp/patterns';
import { useUnits } from '@web-stories-wp/units';
/**
 * Internal dependencies
 */
import { useFont } from '../../app';
import {
  elementFillContent,
  elementWithFont,
  elementWithBackgroundColor,
  elementWithTextParagraphStyle,
  elementWithBorder,
  elementWithHighlightBorderRadius,
  elementWithTextBoxShadow,
  elementWithTextShadow,
} from '../shared';
import StoryPropTypes from '../../types';
import { BACKGROUND_TEXT_MODE } from '../../constants';
import { useTransformHandler } from '../../components/transform';
import { getHTMLFormatters } from '../../components/richText/htmlManipulation';
import {
  getResponsiveBorder,
  shouldDisplayBorder,
} from '../../utils/elementBorder';
import useColorTransformHandler from '../shared/useColorTransformHandler';
import { getHighlightLineheight, generateParagraphTextStyle } from './util';
import {
  calcFontMetrics,
  generateFontFamily,
  getStandardHighlightHeight,
  getSvgTextVerticalPosition,
  shouldSyncSvgTextWithHtml,
  shouldUseSvgTextFill,
} from './utils';
import { SvgTextDisplay } from './svgTextDisplay';
import { useRangeGradientPreview } from './useGradientPreview';
import { getGradientTextStyles } from './gradientTextStyles';
import {
  getMeasuredLineBreakContent,
  stripColorStylesFromMeasuredLineBreakContent,
} from './utils/getMeasuredLineBreakContent';
import {
  getFirstSvgTextRect,
  getFirstTextRect,
} from './utils/getTextRenderRects';
import {
  collectTextFontUsage,
  getBoundedTypographyScale,
  getLiveTypographyFontSizeCss,
  hasEffectiveInlineFontSizeRuns,
  hasEffectiveInlineLineHeightRuns,
  hasMixedTypographyMarkers,
  materializeTextContent,
  parseInlineFontSize,
  scaleInlineFontSizes,
  setLiveTypographyScale,
} from './utils/mixedTypography';
import getListRenderContent from './utils/getListRenderContent';
import { hasSemanticList } from './utils/listStyle';
import NativeTextLayout from './nativeTextLayout';
import useSanitizedTextElement from './utils/useSanitizedTextElement';
import useTextLayoutElement from './utils/useTextLayoutElement';

const OutsideBorder = styled.div`
  ${elementWithBorder}
`;
const HighlightWrapperElement = styled.div`
  ${elementFillContent}
  ${elementWithFont}
  ${elementWithTextParagraphStyle}
  ${({ $usesMixedTypographyLayout, lineHeight, verticalPadding }) =>
    $usesMixedTypographyLayout
      ? css`
          font-size: 0;
          line-height: 0;
        `
      : css`
          line-height: ${getHighlightLineheight(lineHeight, verticalPadding)};
        `}
  padding: 0;
`;
const HighlightElement = styled.p`
  font-size: inherit;
  left: 0;
  line-height: inherit;
  margin: 0;
  position: absolute;
  width: 100%;
`;

const MarginedElement = styled.span`
  position: relative;
  display: block;
  top: 0;
  left: 0;
`;

const Span = styled.span`
  ${elementWithBackgroundColor}
  ${elementWithTextParagraphStyle}

  box-decoration-break: clone;
  position: relative;
`;

const HighlightSpan = styled(Span)`
  padding: ${({ $usesMixedTypographyLayout, padding }) =>
    $usesMixedTypographyLayout ? 0 : padding};

  ${({ $usesMixedTypographyLayout, padding }) =>
    $usesMixedTypographyLayout &&
    css`
      > [data-st-text-line] {
        align-items: center;
        display: inline-flex;
        vertical-align: top;
      }

      > [data-st-text-line] > [data-st-text-line-band] {
        border-radius: inherit;
        box-sizing: border-box;
        box-decoration-break: clone;
        display: inline-block;
        line-height: 1em;
        padding: ${padding};
        position: relative;
      }
    `}
`;

const BackgroundSpan = styled(HighlightSpan)`
  ${elementWithHighlightBorderRadius}
  ${({ $usesMixedTypographyLayout }) =>
    !$usesMixedTypographyLayout && elementWithTextBoxShadow}
  color: transparent;

  ${({ $hasMixedLineHeight, $usesMixedTypographyLayout }) =>
    $usesMixedTypographyLayout &&
    css`
      -webkit-background-clip: text;
      background-clip: text;

      > [data-st-text-line],
      > [data-st-text-line] > [data-st-text-line-band] {
        background: inherit;
      }

      > [data-st-text-line] {
        -webkit-background-clip: text;
        background-clip: text;
      }

      > [data-st-text-line] > [data-st-text-line-band] {
        -webkit-background-clip: border-box;
        background-clip: border-box;
        ${elementWithTextBoxShadow}
      }

      ${$hasMixedLineHeight &&
      css`
        background: none;

        > [data-st-text-line] {
          background: none;
        }

        > [data-st-text-line] > [data-st-text-line-band] {
          ${elementWithBackgroundColor}
        }
      `}
    `}
`;

const ForegroundSpan = styled(HighlightSpan)`
  ${elementWithTextShadow}
  background: none;
  opacity: ${({ opacity }) => opacity};
  ${({ $gradientFill }) => getGradientTextStyles($gradientFill)}
`;

// Using attributes to avoid creation of hundreds of classes by styled components for previewMode.
const FillElement = styled.p.attrs(
  ({
    theme,
    previewMode,
    fontStyle,
    fontSize,
    fontWeight,
    font,
    marginOffsetTop,
    padding,
    lineHeight,
    textAlign,
    dataToEditorY,
    $suppressDefaultFontStrut,
  }) => {
    return previewMode
      ? {
          style: {
            zIndex: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            letterSpacing: 'normal',
            color: theme.colors.standard.black,
            fontStyle,
            fontSize: $suppressDefaultFontStrut ? '0px' : `${fontSize}px`,
            fontWeight,
            fontFamily: generateFontFamily(font),
            margin: `${-dataToEditorY(marginOffsetTop)}px 0`,
            padding: padding || 0,
            lineHeight,
            textAlign,
          },
        }
      : {};
  }
)`
  opacity: ${({ opacity }) => opacity};
  margin: 0;
  ${elementFillContent}
  ${elementWithTextShadow}
  ${({ previewMode }) => !previewMode && elementWithFont}
  ${({ previewMode }) => !previewMode && elementWithTextParagraphStyle}
  ${({ $gradientFill }) => getGradientTextStyles($gradientFill)}
  font-size: ${({ $suppressDefaultFontStrut, fontSize }) =>
    $suppressDefaultFontStrut ? 0 : `${fontSize}px`};
`;

const Background = styled.div`
  ${elementWithBackgroundColor}
  ${elementFillContent}
  ${elementWithBorder}
  ${({ $hasTextBoxBackdrop }) =>
    !$hasTextBoxBackdrop && elementWithTextBoxShadow}
  margin: 0;
`;

function updateInlineFontSizePreview(root, scale, dataToEditorY) {
  root?.querySelectorAll('[data-st-font-size]').forEach((span) => {
    const fontSize = parseInlineFontSize(
      span.getAttribute('data-st-font-size')
    );
    if (fontSize !== null) {
      span.style.fontSize = `${dataToEditorY(fontSize * scale)}px`;
    }
  });
}

function TextDisplayCanvas({
  element: untrustedElement,
  handleDimensionsUpdated,
  previewMode,
  hasTextBoxBackdrop = false,
  svgRenderType = 'display',
  outerToNativeX,
  outerToNativeY,
}) {
  const element = useSanitizedTextElement(untrustedElement);
  const layoutElement = useTextLayoutElement(element);
  const {
    id,
    content,
    backgroundColor,
    backgroundTextMode,
    border,
    borderRadius,
    lastUpdatedBy,
    ...rest
  } = element;
  const hasList = hasSemanticList(content);
  const hasMixedTypography = useMemo(
    () => hasMixedTypographyMarkers(content),
    [content]
  );
  const hasMixedLineHeight = useMemo(
    () => !hasList && hasEffectiveInlineLineHeightRuns(layoutElement),
    [layoutElement, hasList]
  );
  const suppressDefaultFontStrut = useMemo(
    () =>
      !hasList &&
      (hasEffectiveInlineFontSizeRuns(layoutElement) || hasMixedLineHeight),
    [layoutElement, hasList, hasMixedLineHeight]
  );
  const supportedBackgroundTextMode =
    hasList && backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? BACKGROUND_TEXT_MODE.FILL
      : backgroundTextMode;
  const displayElement = useMemo(
    () =>
      supportedBackgroundTextMode === backgroundTextMode
        ? element
        : { ...element, backgroundTextMode: supportedBackgroundTextMode },
    [backgroundTextMode, element, supportedBackgroundTextMode]
  );
  const { loadedFonts = [], maybeEnqueueFontStyle } = useFont(
    ({ actions: { maybeEnqueueFontStyle }, state: { loadedFonts } }) => ({
      loadedFonts,
      maybeEnqueueFontStyle,
    })
  );

  const fontUsage = useMemo(
    () => collectTextFontUsage(layoutElement),
    [layoutElement]
  );
  const usedRenderFamilies = useMemo(
    () => new Set(fontUsage.map(({ renderFamily }) => renderFamily)),
    [fontUsage]
  );
  const fontLoaded = useMemo(
    () =>
      loadedFonts.filter((loadedFont) =>
        usedRenderFamilies.has(
          loadedFont.renderFamily || loadedFont.font.family
        )
      ),
    [loadedFonts, usedRenderFamilies]
  );

  const ref = useRef(null);
  const outerBorderRef = useRef(null);
  const bgRef = useRef(null);
  const fgRef = useRef(null);

  const { dataToEditorX, dataToEditorY, editorToDataX, editorToDataY } =
    useUnits(
      ({
        actions: { dataToEditorX, dataToEditorY, editorToDataX, editorToDataY },
      }) => ({
        dataToEditorX,
        dataToEditorY,
        editorToDataX,
        editorToDataY,
      })
    );

  const { font, width: elementWidth, height: elementHeight } = rest;
  const [requestedTypographyScale, setRequestedTypographyScale] = useState(1);
  const liveTypographyScale = useMemo(
    () => getBoundedTypographyScale(layoutElement, requestedTypographyScale),
    [layoutElement, requestedTypographyScale]
  );
  const previewElement = useMemo(
    () => ({
      ...displayElement,
      fontSize: element.fontSize * liveTypographyScale,
      content: scaleInlineFontSizes(element.content, liveTypographyScale),
    }),
    [displayElement, element.content, element.fontSize, liveTypographyScale]
  );
  const previewLayoutElement = useTextLayoutElement(previewElement);
  const fontFaceSetConfigs = fontUsage;

  const {
    height: verticalTrimHeight,
    marginOffsetTop,
    heightWithoutVerticalTrim,
    standardTrimHeight,
  } = useMemo(
    () =>
      calcFontMetrics({
        element: {
          content: previewElement.content,
          fontReferences: previewElement.fontReferences,
          color: layoutElement.color,
          font: element.font,
          fontSize: previewElement.fontSize,
          fontStyle: element.fontStyle,
          fontWeight: element.fontWeight,
          lineHeight: element.lineHeight,
          verticalTrim: element.verticalTrim,
          width: element.width,
          backgroundTextMode: supportedBackgroundTextMode,
          padding: element.padding,
          listStyle: element.listStyle,
        },
        dataToEditorX,
        dataToEditorY,
      }),
    [
      element.content,
      layoutElement.color,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.verticalTrim,
      element.width,
      supportedBackgroundTextMode,
      element.padding,
      previewElement.content,
      previewElement.fontReferences,
      previewElement.fontSize,
      element.listStyle,
      dataToEditorX,
      dataToEditorY,
      fontLoaded,
    ]
  );

  const props = {
    font,
    element,
    dataToEditorX,
    dataToEditorY,
    marginOffsetTop: dataToEditorY(marginOffsetTop),
    ...(supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.NONE
      ? {}
      : { backgroundColor }),
    ...generateParagraphTextStyle(
      rest,
      dataToEditorX,
      dataToEditorY,
      undefined,
      marginOffsetTop,
      element
    ),
    horizontalPadding: dataToEditorX(rest.padding?.horizontal || 0),
    verticalPadding: dataToEditorX(rest.padding?.vertical || 0),
    textBoxVerticalPadding: dataToEditorY(rest.padding?.vertical || 0),
  };

  useEffect(() => {
    maybeEnqueueFontStyle(fontFaceSetConfigs);
  }, [fontFaceSetConfigs, maybeEnqueueFontStyle]);

  const isHighLight =
    supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT;
  const usesMixedTypographyHighlightLayout = isHighLight && hasMixedTypography;
  const hasGradientFill = useMemo(
    () => hasGradient(element.color),
    [element.color]
  );
  const rangeGradientPreview = useRangeGradientPreview(element.id);
  const hasSvgFill = useMemo(
    () =>
      Boolean(rangeGradientPreview) ||
      shouldUseSvgTextFill({
        element: previewElement,
        hasGradientFill,
        includeMixedTypography: true,
      }),
    [previewElement, hasGradientFill, rangeGradientPreview]
  );
  const refWithBorder = isHighLight ? outerBorderRef : bgRef;
  const [liveSvgSize, setLiveSvgSize] = useState(null);
  const [svgDisplayOffset, setSvgDisplayOffset] = useState({ x: 0, y: 0 });
  const fillBackgroundHeight =
    element.verticalTrim === 'baseline' &&
    supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.FILL &&
    verticalTrimHeight
      ? verticalTrimHeight
      : elementHeight;
  const fillBackgroundStyle =
    fillBackgroundHeight !== elementHeight
      ? { height: `${dataToEditorY(fillBackgroundHeight)}px` }
      : undefined;

  useTransformHandler(id, (transform) => {
    // Ref is set in case of high-light mode only, use the fgRef if that's missing.
    const target = ref?.current || fgRef.current;
    if (!target) {
      return;
    }
    const updatedFontSize = transform?.updates?.fontSize;
    const typographyScale =
      updatedFontSize && element.fontSize
        ? updatedFontSize / element.fontSize
        : 1;
    target.style.fontSize = updatedFontSize
      ? `${dataToEditorY(updatedFontSize)}px`
      : '';
    if (!hasSvgFill && !isHighLight) {
      setLiveTypographyScale(target, typographyScale);
    } else {
      updateInlineFontSizePreview(target, typographyScale, dataToEditorY);
    }
    const isWidthOnlyResize =
      transform?.direction?.[0] !== 0 && transform?.direction?.[1] === 0;
    const shouldRerenderPreview =
      hasSvgFill || (isHighLight && isWidthOnlyResize);
    if (transform && shouldRerenderPreview) {
      setRequestedTypographyScale(typographyScale);
    }
    const updatedMargin = transform
      ? transform.updates?.marginOffsetTop ?? marginOffsetTop * typographyScale
      : null;
    target.style.margin = updatedMargin
      ? `${dataToEditorY(-updatedMargin)}px 0`
      : '';

    if (outerBorderRef.current || bgRef.current) {
      // Depending on the background mode, choose the element that has border assigned to it.
      if (transform) {
        const { resize } = transform;
        if (resize && resize[0] !== 0 && resize[1] !== 0) {
          const width = outerToNativeX(resize[0]);
          const height = outerToNativeY(resize[1]);
          if (shouldRerenderPreview) {
            setLiveSvgSize({ width, height });
          }
          if (shouldDisplayBorder(element)) {
            refWithBorder.current.style.width =
              width + border.left + border.right + 'px';
            refWithBorder.current.style.height =
              height + border.top + border.bottom + 'px';
          }
        }
      } else {
        setLiveSvgSize(null);
        setRequestedTypographyScale(1);
      }
    }
  });

  useColorTransformHandler({
    id,
    targetRef: hasTextBoxBackdrop ? null : bgRef,
    expectedStyle: 'background',
  });
  useColorTransformHandler({ id, targetRef: fgRef, expectedStyle: 'color' });
  useColorTransformHandler({
    id,
    targetRef: refWithBorder,
    expectedStyle: 'border-color',
  });

  useLayoutEffect(() => {
    if (previewMode) {
      return;
    }

    const standardHighlightHeight = getStandardHighlightHeight({
      backgroundTextMode: supportedBackgroundTextMode,
      highlightElement: bgRef.current,
      verticalTrim: element.verticalTrim,
    });
    const calculatedMixedHeight =
      element.verticalTrim === 'baseline'
        ? verticalTrimHeight
        : standardTrimHeight;
    const height =
      standardHighlightHeight !== null
        ? editorToDataY(standardHighlightHeight)
        : hasMixedTypography && Number.isFinite(calculatedMixedHeight)
        ? calculatedMixedHeight
        : null;
    if (height === null) {
      return;
    }

    if (Math.abs(elementHeight - height) < 0.01) {
      return;
    }

    handleDimensionsUpdated?.({
      height,
      width: elementWidth,
    });
  }, [
    editorToDataY,
    element.verticalTrim,
    elementHeight,
    elementWidth,
    fontLoaded,
    hasMixedTypography,
    handleDimensionsUpdated,
    previewMode,
    previewElement.content,
    standardTrimHeight,
    supportedBackgroundTextMode,
    verticalTrimHeight,
  ]);

  useEffect(() => {
    // If the copy was updated by the user accepting a suggestion
    // we need to recalculate the dimensions
    if (!previewMode && lastUpdatedBy === 'suggestion') {
      const textContainer = fgRef.current;

      fgRef.current.style.height = 'auto';

      handleDimensionsUpdated?.({
        height: editorToDataY(textContainer.offsetHeight),
        width: editorToDataX(textContainer.offsetWidth),
      });

      fgRef.current.style.height = '100%';
    }
  }, [
    editorToDataX,
    editorToDataY,
    handleDimensionsUpdated,
    lastUpdatedBy,
    previewMode,
  ]);

  // Mixed typography already uses the same deterministic line geometry as the
  // generated output. Syncing it back to an HTML clone reintroduces browser
  // font-metric drift, especially for negative leading and highlights.
  const shouldSyncSvgDisplayOffset = shouldSyncSvgTextWithHtml({
    element: previewElement,
    hasSvgFill,
    isHighlight: isHighLight,
  });
  const gradientFill = useMemo(
    () => (hasGradientFill && !hasSvgFill ? element.color : null),
    [element.color, hasGradientFill, hasSvgFill]
  );
  const measuredLineBreakContent = useMemo(
    () =>
      (hasSvgFill || isHighLight || hasMixedLineHeight) && !hasList
        ? getMeasuredLineBreakContent({
            element: previewLayoutElement,
            dataToEditorX,
            dataToEditorY,
            fontSizeAttributeScale: liveTypographyScale,
            materializeFontSizes: suppressDefaultFontStrut,
            materializeLineHeights: hasMixedLineHeight,
            lineHeightCss: usesMixedTypographyHighlightLayout
              ? getHighlightLineheight(
                  element.lineHeight,
                  dataToEditorY(element.padding?.vertical || 0)
                )
              : suppressDefaultFontStrut
              ? getHighlightLineheight(element.lineHeight, 0)
              : undefined,
            lineBandHeightCss: usesMixedTypographyHighlightLayout
              ? getHighlightLineheight(
                  1,
                  dataToEditorY(element.padding?.vertical || 0)
                )
              : undefined,
            preserveColor: isHighLight && !hasSvgFill,
            verticalPadding: dataToEditorY(element.padding?.vertical || 0),
            wrapLines: usesMixedTypographyHighlightLayout,
            width: liveSvgSize?.width ?? dataToEditorX(elementWidth),
          })
        : null,
    [
      dataToEditorX,
      dataToEditorY,
      previewLayoutElement,
      elementWidth,
      fontLoaded,
      hasSvgFill,
      isHighLight,
      liveSvgSize,
      liveTypographyScale,
      hasList,
      hasMixedLineHeight,
      element.lineHeight,
      element.padding?.vertical,
      suppressDefaultFontStrut,
      usesMixedTypographyHighlightLayout,
    ]
  );
  const measuredLineBreakContentWithoutColor = useMemo(
    () =>
      isHighLight && !hasSvgFill && measuredLineBreakContent
        ? stripColorStylesFromMeasuredLineBreakContent(measuredLineBreakContent)
        : measuredLineBreakContent,
    [hasSvgFill, isHighLight, measuredLineBreakContent]
  );
  const materializedContent = useMemo(
    () =>
      materializeTextContent(layoutElement, {
        fontSizeToCss: (size) =>
          !hasSvgFill && !isHighLight
            ? getLiveTypographyFontSizeCss(size, dataToEditorY)
            : `${dataToEditorY(size * liveTypographyScale)}px`,
      }),
    [dataToEditorY, layoutElement, hasSvgFill, isHighLight, liveTypographyScale]
  );
  const displayContent = measuredLineBreakContent || materializedContent;
  // Setting the text color of the entire block to black essentially removes all inline
  // color styling allowing us to apply transparent to all of them.
  const contentWithoutColor = useMemo(
    () =>
      measuredLineBreakContentWithoutColor ||
      materializeTextContent(
        {
          ...layoutElement,
          content: getHTMLFormatters().setColor(
            layoutElement.content,
            createSolid(0, 0, 0)
          ),
        },
        {
          fontSizeToCss: (size) =>
            !hasSvgFill && !isHighLight
              ? getLiveTypographyFontSizeCss(size, dataToEditorY)
              : `${dataToEditorY(size * liveTypographyScale)}px`,
        }
      ),
    [
      dataToEditorY,
      layoutElement,
      hasSvgFill,
      isHighLight,
      liveTypographyScale,
      measuredLineBreakContentWithoutColor,
    ]
  );
  const renderListContent = useMemo(
    () =>
      (value, options = {}) =>
        getListRenderContent({
          content: value,
          color: element.color,
          listStyle: element.listStyle,
          font: element.font,
          fontMetrics: element.font?.metrics,
          fontSize: element.fontSize,
          fontStyle: element.fontStyle,
          fontWeight: element.fontWeight,
          lineHeight: element.lineHeight,
          dataToStyleX: (size) => `${dataToEditorX(size)}px`,
          dataToStyleY: (size) => `${dataToEditorY(size)}px`,
          dataToFontSizeY: (size) => `${dataToEditorY(size)}px`,
          dataToOrderedMarkerStyleX: (size) => `${dataToEditorY(size)}px`,
          ...options,
        }),
    [
      dataToEditorX,
      dataToEditorY,
      element.color,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.listStyle,
    ]
  );
  const renderedDisplayContent = useMemo(
    () => renderListContent(displayContent),
    [displayContent, renderListContent]
  );
  const renderedContentWithoutColor = useMemo(
    () =>
      renderListContent(contentWithoutColor, {
        hideMarkers: true,
      }),
    [contentWithoutColor, renderListContent]
  );
  const { marginTop: svgTextMarginTop, top: svgTextTop } =
    getSvgTextVerticalPosition({
      backgroundTextMode: supportedBackgroundTextMode,
      marginOffsetTop: props.marginOffsetTop,
      verticalPadding: props.textBoxVerticalPadding,
      verticalTrim: element.verticalTrim,
    });
  const svgProps = useMemo(
    () => ({
      type: svgRenderType,
      cachePreview: !previewMode,
      element: previewElement,
      textureOrigin: {
        x: -props.horizontalPadding - svgDisplayOffset.x,
        y:
          -svgTextTop -
          svgTextMarginTop -
          svgDisplayOffset.y +
          (isHighLight ? props.marginOffsetTop : 0),
      },
      fillGradientBounds:
        supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT &&
        hasGradientFill
          ? {
              x: -props.horizontalPadding,
              y: -dataToEditorY(rest.padding?.vertical || 0),
              width: liveSvgSize?.width ?? dataToEditorX(elementWidth),
              height: liveSvgSize?.height ?? dataToEditorY(elementHeight),
            }
          : undefined,
      font,
      fontLoaded,
      height:
        liveSvgSize?.height ??
        (element.verticalTrim === 'baseline'
          ? dataToEditorY(heightWithoutVerticalTrim)
          : undefined),
      left: props.horizontalPadding,
      dataToEditorX,
      dataToEditorY,
      fontSize: props.fontSize * liveTypographyScale,
      lineHeight: element.lineHeight,
      width: liveSvgSize?.width,
      styles: {
        margin: svgTextMarginTop ? `${svgTextMarginTop}px 0` : undefined,
        transform:
          svgDisplayOffset.x || svgDisplayOffset.y
            ? `translate(${svgDisplayOffset.x}px, ${svgDisplayOffset.y}px)`
            : undefined,
      },
    }),
    [
      svgRenderType,
      previewMode,
      previewElement,
      supportedBackgroundTextMode,
      elementHeight,
      elementWidth,
      element.lineHeight,
      element.verticalTrim,
      font,
      hasGradientFill,
      props.horizontalPadding,
      dataToEditorX,
      dataToEditorY,
      heightWithoutVerticalTrim,
      liveSvgSize,
      liveTypographyScale,
      svgDisplayOffset,
      svgTextMarginTop,
      svgTextTop,
      isHighLight,
      props.marginOffsetTop,
      rest.padding?.vertical,
      props.fontSize,
      fontLoaded,
    ]
  );

  useLayoutEffect(() => {
    if (!shouldSyncSvgDisplayOffset) {
      setSvgDisplayOffset((currentOffset) =>
        currentOffset.x === 0 && currentOffset.y === 0
          ? currentOffset
          : { x: 0, y: 0 }
      );
      return undefined;
    }

    const syncSvgDisplayOffset = () => {
      const textRect = getFirstTextRect(fgRef.current);
      const svgRect = getFirstSvgTextRect(ref.current?.querySelector('svg'));

      if (!textRect || !svgRect) {
        return;
      }

      setSvgDisplayOffset((currentOffset) => {
        const nextOffset = {
          x: currentOffset.x + outerToNativeX(textRect.x - svgRect.x),
          y: currentOffset.y + outerToNativeY(textRect.y - svgRect.y),
        };

        if (
          Math.abs(currentOffset.x - nextOffset.x) < 0.5 &&
          Math.abs(currentOffset.y - nextOffset.y) < 0.5
        ) {
          return currentOffset;
        }

        return nextOffset;
      });
    };

    syncSvgDisplayOffset();

    const animationFrameId = requestAnimationFrame(syncSvgDisplayOffset);

    return () => cancelAnimationFrame(animationFrameId);
  }, [
    displayContent,
    element.verticalTrim,
    fontLoaded,
    liveSvgSize,
    outerToNativeX,
    outerToNativeY,
    shouldSyncSvgDisplayOffset,
  ]);

  if (isHighLight) {
    // We need a separate outside border wrapper for outside border
    // since the highlight wrapper uses negative margin to position the content.
    // This, however, would shift the border incorrectly.
    return (
      <OutsideBorder
        ref={outerBorderRef}
        border={getResponsiveBorder(border, previewMode, dataToEditorX)}
        borderRadius={borderRadius}
        width={elementWidth}
        height={elementHeight}
      >
        <HighlightWrapperElement
          ref={ref}
          {...props}
          $usesMixedTypographyLayout={usesMixedTypographyHighlightLayout}
          marginOffsetTop={marginOffsetTop}
        >
          <HighlightElement {...props}>
            <MarginedElement {...props}>
              <BackgroundSpan
                ref={bgRef}
                {...props}
                $hasMixedLineHeight={hasMixedLineHeight}
                $usesMixedTypographyLayout={usesMixedTypographyHighlightLayout}
                borderRadius={borderRadius}
                dataToEditorY={dataToEditorY}
                dangerouslySetInnerHTML={{
                  __html: renderedContentWithoutColor,
                }}
                marginOffsetTop={marginOffsetTop}
              />
            </MarginedElement>
          </HighlightElement>
          <HighlightElement {...props}>
            <MarginedElement {...props}>
              <ForegroundSpan
                ref={fgRef}
                {...props}
                $usesMixedTypographyLayout={usesMixedTypographyHighlightLayout}
                $gradientFill={gradientFill}
                dangerouslySetInnerHTML={{
                  __html: renderedDisplayContent,
                }}
                opacity={hasSvgFill ? 0 : 1}
                marginOffsetTop={marginOffsetTop}
              />
              {hasSvgFill && (
                <SvgTextDisplay
                  initialGradientPreview={rangeGradientPreview}
                  paddingBetweenLines={props.verticalPadding}
                  top={svgTextTop}
                  {...svgProps}
                />
              )}
            </MarginedElement>
          </HighlightElement>
        </HighlightWrapperElement>
      </OutsideBorder>
    );
  }

  return (
    <Background
      ref={bgRef}
      backgroundColor={
        supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.FILL &&
        !hasTextBoxBackdrop &&
        backgroundColor
      }
      borderRadius={borderRadius}
      border={getResponsiveBorder(border, previewMode, dataToEditorX)}
      element={element}
      dataToEditorX={dataToEditorX}
      dataToEditorY={dataToEditorY}
      $hasTextBoxBackdrop={hasTextBoxBackdrop}
      width={elementWidth}
      height={fillBackgroundHeight}
      style={fillBackgroundStyle}
    >
      <FillElement
        as={hasList ? 'div' : undefined}
        ref={fgRef}
        $gradientFill={gradientFill}
        dangerouslySetInnerHTML={{
          __html: renderedDisplayContent,
        }}
        previewMode={previewMode}
        {...props}
        marginOffsetTop={marginOffsetTop}
        opacity={hasSvgFill ? 0 : 1}
        $suppressDefaultFontStrut={suppressDefaultFontStrut}
      />
      {hasSvgFill && (
        <SvgTextDisplay
          initialGradientPreview={rangeGradientPreview}
          top={svgTextTop}
          {...svgProps}
        />
      )}
    </Background>
  );
}

TextDisplayCanvas.propTypes = {
  element: StoryPropTypes.elements.text.isRequired,
  box: StoryPropTypes.box.isRequired,
  previewMode: PropTypes.bool,
  hasTextBoxBackdrop: PropTypes.bool,
  svgRenderType: PropTypes.oneOf(['display', 'edit']),
  outerToNativeX: PropTypes.func.isRequired,
  outerToNativeY: PropTypes.func.isRequired,
};

function TextDisplay(props) {
  return (
    <NativeTextLayout>
      {({ toNativeX, toNativeY }) => (
        <TextDisplayCanvas
          {...props}
          outerToNativeX={toNativeX}
          outerToNativeY={toNativeY}
        />
      )}
    </NativeTextLayout>
  );
}

TextDisplay.propTypes = {
  element: StoryPropTypes.elements.text.isRequired,
  box: StoryPropTypes.box.isRequired,
  previewMode: PropTypes.bool,
  hasTextBoxBackdrop: PropTypes.bool,
  svgRenderType: PropTypes.oneOf(['display', 'edit']),
};

export default TextDisplay;
